import {
  ButtonVariant,
  LoadingButtonVariant
} from '@aurora/shared-client/components/common/Button/enums';
import ConfirmationDialog from '@aurora/shared-client/components/common/ConfirmationDialog/ConfirmationDialog';
import {
  ToastAlertVariant,
  ToastVariant
} from '@aurora/shared-client/components/common/ToastAlert/enums';
import type ToastProps from '@aurora/shared-client/components/common/ToastAlert/ToastAlertProps';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useToasts from '@aurora/shared-client/components/context/ToastContext/useToasts';
import InputEditForm from '@aurora/shared-client/components/form/InputEditForm/InputEditForm';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import isSsoUserQuery from '@aurora/shared-client/components/users/IsSsoUser.query.graphql';
import FormBuilder from '@aurora/shared-client/helpers/form/FormBuilder/FormBuilder';
import handleErrorStates from '@aurora/shared-client/helpers/form/MutationError/MutationErrorHelper';
import type { Form } from '@aurora/shared-generated/types/graphql-schema-types';
import type {
  ContextUserFragment,
  IsSsoUserQuery,
  IsSsoUserQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import { checkPolicy } from '@aurora/shared-utils/helpers/objects/PolicyResultHelper';
import { getLog } from '@aurora/shared-utils/log';
import React, { type ReactElement, useContext, useId } from 'react';
import { Modal, useClassNameMapper } from 'react-bootstrap';
import type { ErrorOption } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import { useUIDSeed } from 'react-uid';
import type {
  CloseUserAccountMutation,
  CloseUserAccountMutationVariables
} from '../../../../types/graphql-types';
import useTranslation from '../../../useTranslation';
import formSchema from '../CloseAccountAction.form.json';
import closeUserAccountMutation from '@aurora/shared-client/components/users/CloseUserAccount.mutation.graphql';

const log = getLog(module);

interface FormData {
  password: string;
}

interface Props {
  /**
   * Whether the modal is currently being displayed
   */
  show: boolean;
  /**
   * Callback function when the modal is hidden or cancelled
   *
   * @callback
   */
  onHide: () => void;
  /**
   * Callback function when user account has been successfully closed
   *
   * @callback
   * @param username the user whose account was closed
   */
  onAccountClosed: (username: string) => void;
  /**
   * The user whose account is to be closed
   */
  contextUser: ContextUserFragment;
}

/**
 * Modal to confirm closing a user's account
 *
 * @author Rishabh Jivan
 */
const CloseUserAccountModal: React.FC<React.PropsWithChildren<Props>> = ({
  show,
  onHide,
  onAccountClosed,
  contextUser
}: Props) => {
  const uid = useId();
  const uidSeed = useUIDSeed();
  const i18n = useTranslation(EndUserComponent.CLOSE_USER_ACCOUNT_MODAL);
  const { formatMessage, loading: i18nLoading } = i18n;
  const cx = useClassNameMapper();
  const { addToast } = useToasts();
  const [closeUserAccountMutate] = useMutationWithTracing<
    CloseUserAccountMutation,
    CloseUserAccountMutationVariables
  >(module, closeUserAccountMutation);
  const { authUser } = useContext(AppContext);
  const isOwnAccount = contextUser.id === authUser.id;

  const { data: isSsoUserData, loading: isSsoUserLoading } = useQueryWithTracing<
    IsSsoUserQuery,
    IsSsoUserQueryVariables
  >(module, isSsoUserQuery);

  if (i18nLoading || isSsoUserLoading) {
    return null;
  }

  const {
    self: {
      userPolicies: { isSsoUser }
    }
  } = isSsoUserData;

  const formSpec = new FormBuilder<FormData>('CloseAccountForm', i18n, {
    schema: formSchema as Form,
    cx
  })
    .addPasswordField({
      name: 'password',
      validations: {
        required: true
      },
      passwordViewable: true,
      defaultValue: ''
    })
    .addSubmitAction({
      title: formatMessage('submit'),
      variant: ButtonVariant.DANGER,
      buttonType: LoadingButtonVariant.LOADING_BUTTON,
      size: 'lg'
    })
    .addCancelAction({ title: formatMessage('cancel'), size: 'lg' })
    .build();

  /**
   * Renders success/failure toast messages
   * @param alertVariant
   * @param title
   * @param body
   * @param autohide
   */
  function renderToast(
    alertVariant: ToastAlertVariant,
    title: string,
    body: string,
    autohide = true
  ): void {
    const toastVariant =
      alertVariant === ToastAlertVariant.DANGER ? ToastVariant.BANNER : ToastVariant.FLYOUT;
    const toastProps: ToastProps = {
      id: uidSeed(title),
      toastVariant,
      alertVariant,
      title,
      autohide,
      message: body,
      delay: 4000
    };
    addToast(toastProps);
  }

  /**
   * Function that handles the close account action
   * @param formData
   * @param setError
   */
  async function closeUserAccount(
    formData?: FormData,
    setError?: (name: FieldPath<FormData>, error: ErrorOption) => void
  ) {
    const username = contextUser.login;
    const mutationOptions: CloseUserAccountMutationVariables = {
      id: contextUser.id
    };

    if (formData) {
      mutationOptions.password = formData.password;
    }

    const { errors, data: response } = await closeUserAccountMutate({
      variables: mutationOptions
    });

    if (errors?.length > 0 || (checkPolicy(isSsoUser) && response?.closeUserAccount.errors)) {
      log.error('Unable to close this account', errors);
      renderToast(
        ToastAlertVariant.DANGER,
        formatMessage('unexpected.error.title'),
        formatMessage('unexpected.error.message')
      );
      return null;
    }

    if (!checkPolicy(isSsoUser) && response?.closeUserAccount.errors) {
      handleErrorStates(response.closeUserAccount.errors, setError);
      return null;
    }
    onAccountClosed(username);
  }

  /**
   * Function that handles the onSubmit action
   * @param formData
   * @param action
   * @param event
   * @param setError
   */
  async function onSubmit(
    formData: FormData,
    action: string,
    event: React.FormEvent<HTMLFormElement>,
    setError: (name: FieldPath<FormData>, error: ErrorOption) => void
  ): Promise<void> {
    if (action === 'cancel') {
      onHide();
      return;
    } else {
      await closeUserAccount(formData, setError);
    }
  }

  /**
   * Function to render the confirmation dialog, when closing account of SSO user.
   */
  function renderConfirmationDialogForSsoUsers(): ReactElement {
    return (
      <ConfirmationDialog
        show={show && checkPolicy(isSsoUser)}
        onHide={onHide}
        onSubmit={() => closeUserAccount()}
        onCancel={(): void => onHide()}
        titleText={formatMessage('modalTitle', { isOwnAccount })}
        bodyText={formatMessage('modalBodyQuestion', { isOwnAccount })}
        submitButtonVariant={ButtonVariant.DANGER}
        submitButtonType={LoadingButtonVariant.LOADING_BUTTON}
        submitButtonText={formatMessage('submit')}
      />
    );
  }

  /**
   * Function to render modal to enter password, when closing account of non-SSO user.
   */
  function renderModalForNonSSOUsers(): ReactElement {
    return (
      <Modal
        show={show}
        onHide={onHide}
        size="sm"
        centered
        aria-labelledby={uid}
        data-testid="CloseUserAccountModal"
        backdrop="static"
      >
        <Modal.Header className={cx('d-inline-block text-center lia-g-mr-0')}>
          <Modal.Title id={uid}>{formatMessage('modalTitle', { isOwnAccount })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className={cx('lia-g-mb-30 text-center')}>
            {formatMessage('modalBodyQuestion', { isOwnAccount })}
          </p>
          <InputEditForm<FormData> formSpec={formSpec} onSubmit={onSubmit} />
        </Modal.Body>
      </Modal>
    );
  }

  return checkPolicy(isSsoUser)
    ? renderConfirmationDialogForSsoUsers()
    : renderModalForNonSSOUsers();
};

export default CloseUserAccountModal;
